import React, { Fragment, forwardRef, useRef, useEffect } from 'react'
import { graphql, parsePath } from 'gatsby'
import Img from 'gatsby-image'

import { Power3 } from '../lib/gsap'

import useInView from '../hooks/useInView'
import { useControlledTimeline } from '../hooks/animation'

import { useNavbarTheme } from '../contexts/NavbarTheme'
import useScrolledBelow from '../hooks/useScrolledBelow'
import theme from '../styles/theme'
import { rem, min, media, lines } from '../styles/tools'

import SEO from '../components/SEO'
import Shell from '../components/Shell'
import Wrapper, { wrapperPadding } from '../components/Wrapper'
import Link from '../components/Link'
import ContentColumn from '../components/ContentColumn'
import { ContentAboveFooter } from '../components/Footer'
import ChallengeUsFooter from '../components/ChallengeUsFooter'
import HelloFab from '../components/HelloFab'
import IntroText from '../components/IntroText'
import BackgroundText from '../components/BackgroundText'
import WordsRotator from '../components/WordsRotator'
import NextArrow from '../components/NextArrow'
import ShowCells from '../components/ShowCells'
import LogoCell from '../components/LogoCell'
import AwardCell from '../components/AwardCell'
import Space from '../components/Space'
import ProjectTile from '../components/ProjectTile'
import { Headline, WideText } from '../styles/typography'
import { AutoScale, AutoSlide } from '../components/Animated'

import { getProject, projectURL } from '../projects'

import topClutchLogo from '../assets/logos/top-clutch-logo.svg'

import acHotelsLogo from '../assets/logos/ac-hotels-logo.svg'
import amc from '../assets/logos/amc.svg'
import dignityHealth from '../assets/logos/dignity-health.svg'
import espnLogo from '../assets/logos/espn-logo.svg'
import foxLogo from '../assets/logos/fox-logo.svg'
import motiv from '../assets/logos/motiv.svg'
import paramount from '../assets/logos/paramount.svg'
import tennisChannel from '../assets/logos/tennis-channel.svg'

import webbyAwards from '../assets/awards/webby-awards.svg'
import addyAwards from '../assets/awards/addy-awards.svg'
import how from '../assets/awards/how.svg'
import W3Awards from '../assets/awards/W3-awards.svg'

const Header = () => {
  const isBelow = useScrolledBelow(20)

  return (
    <header
      css={{
        position: 'relative',
        zIndex: 1,
        color: isBelow ? theme.colors.white : theme.colors.black,
        backgroundColor: isBelow ? theme.colors.grey_1000 : theme.colors.white,
        transitionProperty: 'color, background-color',
        transitionDuration: '1s',
      }}
    >
      <ContentColumn
        css={{
          display: 'flex',
          alignItems: 'center',
          minHeight: rem(320),
          height: '65vh',
          [media.laptop]: { height: '85vh' },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          css={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0.2,
            transition: 'opacity 1s',
            ...lines('#666'),
            willChange: 'opacity',
          }}
        />
        <div css={[wrapperPadding, { width: '100%', paddingRight: 0 }]}>
          <IntroText>
            <Space y={{ mobile: 30, tablet: 0 }} />
            FOR THOSE <br css={{ [media.mobileLg]: { display: 'none' } }} />
            WHO <br css={{ [media.max.mobileLg]: { display: 'none' } }} />
            Dare <br css={{ [media.mobileLg]: { display: 'none' } }} />
            to <WordsRotator words={['compete.', 'win.', 'dominate.']} />
            <Space y={{ laptop: 50 }} />
          </IntroText>
        </div>
      </ContentColumn>
    </header>
  )
}

const CheckMark = forwardRef((props, ref) => {
  const mask = useRef(`mask${Math.random()}`)

  return (
    <svg
      viewBox="0 0 63.8 61.7"
      css={{
        fill: 'currentColor',
        width: '1.5em',
        margin: '0 0 -3px 5px',
      }}
      {...props}
    >
      <mask id={mask.current}>
        <path
          ref={ref}
          fill="none"
          stroke="#fff"
          strokeWidth="7.5"
          strokeMiterlimit="10"
          d="M3.2 30.2L19 54.8C30.5 36.9 44.7 20.2 61 2.5"
        />
      </mask>
      <path
        mask={`url(#${mask.current})`}
        d="M20.9 58c-2 .5-3.8.2-5-2-.6-1.1-1.3-2.2-1.9-3.3-2.5-4.8-5-9.6-7.6-14.4-1.3-2.4-2.1-5-4.9-6.3C3 30.9 4 30.7 6.6 30.7c.7 1.4 1.4 2.8 2.2 4.1.6 1 1.3 1.8 1.9 2.7.9 1.2 1.7 2.4 2.2 3.9.4 1.4 1.6 2.5 2.5 3.7 1 1.4 2.1 2.8 3.3 4.3 1.3-.8 2.3-1.6 2.7-3.4.4-1.6 1.9-3 3-4.5.4-.5 1-.9 1.2-1.5 1-3.1 3.5-5.1 5.3-7.6 1.3-1.8 2.7-3.5 4.2-5.2 1.8-2.2 3.3-4.7 5.4-6.5.2-.2.4-.3.5-.6.7-2.1 2.5-3 4.3-4 .9-.5 1.5-1 1.7-2.1.3-1.2.7-2.5 2.1-2.9.4-.1.6-1 .9-1.5.5-.9.8-2 1.5-2.6 1.8-1.6-3.2 4.9-1.7 3 1.2-1.5 2.7-2.7 4-4 .9-.9 1.8-1.9 2.4-3.1 1.1-.3 2.2-.6 3.4-.9-.1 1.3.8 1.9 1.9 2.2.9.2 1.2.6 1 1.5-1 .7-2 .6-3.1 0-.4-.2-1-.5-1.3-.4-.6.2-1.3.7-1.5 1.3-.2.7.3 1.3 1.2 1.4.6 0 1.2.2 1.7.3 0 .4.1.7 0 .7-1.8 1.2 4.2-4.9 3-3.3s-2.6 2.7-4.4 2.8c-1.2 2-2.3 3.9-3.5 5.7-.5-.1-1-.2-1.7-.3.3.9.5 1.5.6 1.9-.6.9-1.2 1.8-1.9 2.5-.8.9-1.8 1.7-2.7 2.6-.7.7-1.7 1.4-2 2.3-.8 2.3-2.7 3.5-4.5 4.8-.9.7-1.7 1.4-2 2.6-.1.5-6.3 8-6.7 8.4-.4.3-.9.5-1.1.9-1.4 2.8-3.6 5-5.6 7.3-1.5 1.8-2.6 4-3.7 6-1 1.6-1.6 3.2-2.4 4.8z"
      />
    </svg>
  )
})

const CheckPoints = props => {
  const [inViewRef, inView] = useInView()
  const tl = useControlledTimeline({ delay: 0.3 }, inView)
  const line01 = useRef()
  const line02 = useRef()
  const line03 = useRef()
  const link = useRef()
  const path01 = useRef()
  const path02 = useRef()
  const path03 = useRef()

  useEffect(() => {
    const lines = [line01.current, line02.current, line03.current, link.current]
    const paths = [path01.current, path02.current, path03.current]

    tl.staggerFrom(lines, 1, { y: 50, opacity: 0, ease: Power3.easeOut }, 0.2)
    tl.staggerFrom(
      paths,
      0.6,
      { drawSVG: '0%', ease: Power3.easeInOut },
      0.3,
      '-=.5'
    )
  }, [])

  return (
    <div ref={inViewRef} {...props}>
      <ul>
        <li ref={line01}>
          Challenge Conventions <CheckMark ref={path01} />
        </li>
        <li ref={line02}>
          Challenge Assumptions <CheckMark ref={path02} />
        </li>
        <li ref={line03}>
          Challenge Market Leaders <CheckMark ref={path03} />
        </li>
      </ul>
      <Space y={theme.space.lg} />
      <WideText as={Link} to="/agency" ref={link}>
        MEET OUR TEAM
      </WideText>
    </div>
  )
}

const AgencySection = ({ images: { davidFace }, ...props }) => (
  <Wrapper padded as="section" css={{ overflow: 'hidden' }} {...props}>
    <BackgroundText>AGENCY</BackgroundText>
    <AutoSlide
      left
      css={{
        position: 'relative',
        zIndex: 2,
        marginTop: rem(20),
        [media.tabletMd]: {
          marginTop: '-6.35vmax',
          marginBottom: rem(40),
          marginLeft: rem(-5),
        },
      }}
    >
      <Headline
        css={{
          maxWidth: '11em',
          fontSize: rem(22),
          [media.tabletMd]: { fontSize: '4.05vmax' },
        }}
      >
        We're a creative marketing agency for those who seek to:
      </Headline>
    </AutoSlide>
    <div
      css={{
        display: 'grid',
        alignItems: 'start',
        [media.tabletMd]: { gridTemplateColumns: 'repeat(8, 1fr)' },
      }}
    >
      <CheckPoints
        css={{
          [media.max.tablet]: { display: 'none' },
          zIndex: 1,
          gridArea: '1 / 1 / 1 / 6',
          [media.laptopLg]: { gridArea: '1 / 1 / 1 / 5' },
          fontWeight: 700,
          fontSize: rem(18),
          [media.laptop]: { fontSize: '1.45vw' },
          lineHeight: 1.6,
        }}
      />
      <AutoScale
        scale={0.9}
        delay={400}
        css={{
          gridArea: '1 / 5 / 1 / 9',
          [media.max.tabletMd]: { display: 'none' },
          marginTop: '-16vw',
        }}
      >
        <Img fluid={davidFace} />
      </AutoScale>
    </div>
    <Space y={theme.space.md} />
  </Wrapper>
)

const ClientLogosSection = props => (
  <Wrapper padded as="section" css={{ overflow: 'hidden' }} {...props}>
    <BackgroundText>CLIENTS</BackgroundText>
    <AutoSlide
      left
      css={{
        [media.max.tabletMd]: { display: 'none' },
        position: 'relative',
        zIndex: 2,
        marginTop: rem(20),
        [media.tabletMd]: {
          marginTop: '-6.35vmax',
          marginBottom: rem(40),
          marginLeft: rem(-5),
        },
      }}
    >
      <Headline>
        Those who <br css={{ [media.tabletMd]: { display: 'none' } }} />
        challenged us.
      </Headline>
    </AutoSlide>
    <ShowCells>
      <div
        css={{
          display: 'grid',
          padding: rem(5),
          gridGap: rem(10),
          gridTemplateColumns: '1fr 1fr',
          [media.tablet]: {
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
          },
        }}
      >
        <Link to="/work/ac-hotels">
          <LogoCell color="#c6c4b9">
            <img
              width="46%"
              style={{ transform: 'scale(.8)' }}
              src={acHotelsLogo}
              alt="AC hotels logo"
            />
          </LogoCell>
        </Link>
        <Link to="/work/amc-preacher">
          <LogoCell color="#ffcd38">
            <img
              width="60%"
              style={{ transform: 'scale(.8)' }}
              src={amc}
              alt="AMC logo"
            />
          </LogoCell>
        </Link>
        <Link to="/work/dignity-health">
          <LogoCell color="#f98d3d">
            <img
              width="85%"
              style={{ transform: 'scale(.8)' }}
              src={dignityHealth}
              alt="Dignity Health logo"
            />
          </LogoCell>
        </Link>
        <Link to="/work/espn">
          <LogoCell color="#881110">
            <img
              width="60%"
              style={{ transform: 'scale(.8)' }}
              src={espnLogo}
              alt="ESPN logo"
            />
          </LogoCell>
        </Link>
        <Link to="/work/fox-digital">
          <LogoCell color="#d7d74c">
            <img
              width="50%"
              style={{ transform: 'scale(.8)' }}
              src={foxLogo}
              alt="Fox logo"
            />
          </LogoCell>
        </Link>
        <Link to="/work/motiv-ring">
          <LogoCell color="#2b2e2e">
            <img
              width="75%"
              style={{ transform: 'scale(.8)' }}
              src={motiv}
              alt="Motiv logo"
            />
          </LogoCell>
        </Link>
        <Link to="/work/paramount-equity-mortgage">
          <LogoCell color="#073c8b">
            <img
              width="70%"
              style={{ transform: 'scale(.8)' }}
              src={paramount}
              alt="Paramount logo"
            />
          </LogoCell>
        </Link>
        <Link to="/work/tennis-channel">
          <LogoCell color="#008568">
            <img
              width="40%"
              style={{ transform: 'scale(.8)' }}
              src={tennisChannel}
              alt="Tennis Channel logo"
            />
          </LogoCell>
        </Link>
      </div>
    </ShowCells>
    <Space y={{ mobile: 20, laptop: 40, laptopLg: 50 }} />
    <AutoSlide
      up
      translate={30}
      css={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: rem(30), // needed because of overflow: 'hidden on the parent element
      }}
    >
      <WideText as={Link} to="/clients">
        Brands we work with
      </WideText>
    </AutoSlide>
  </Wrapper>
)

const AwardLogosSection = props => (
  <Wrapper padded as="section" css={{ overflow: 'hidden' }} {...props}>
    <BackgroundText>AWARDS</BackgroundText>
    <AutoSlide
      left
      css={{
        [media.max.tabletMd]: { display: 'none' },
        position: 'relative',
        zIndex: 2,
        marginTop: rem(20),
        [media.tabletMd]: {
          marginTop: '-6.35vmax',
          marginBottom: rem(40),
          marginLeft: rem(-5),
        },
      }}
    >
      <Headline>Those who recognized us.</Headline>
    </AutoSlide>
    <ShowCells>
      <div
        css={{
          display: 'grid',
          padding: rem(5),
          gridGap: rem(10),
          gridTemplateColumns: '1fr 1fr',
          [media.tablet]: {
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
          },
        }}
      >
        <LogoCell>
          <AwardCell count={1}>
            <img
              width="60%"
              style={{ transform: 'scale(.8)' }}
              src={webbyAwards}
              alt="Webby Awards logo"
            />
          </AwardCell>
        </LogoCell>
        <LogoCell>
          <AwardCell count={1}>
            <img
              width="60%"
              style={{ transform: 'scale(.8)' }}
              src={addyAwards}
              alt="Addy Awards logo"
            />
          </AwardCell>
        </LogoCell>
        <LogoCell>
          <AwardCell count={1}>
            <img
              width="60%"
              style={{ transform: 'scale(.8)' }}
              src={how}
              alt="How Awards logo"
            />
          </AwardCell>
        </LogoCell>
        <LogoCell>
          <AwardCell count={10}>
            <img
              width="37%"
              style={{ transform: 'scale(.8)' }}
              src={W3Awards}
              alt="W3 Awards logo"
            />
          </AwardCell>
        </LogoCell>
      </div>
    </ShowCells>
    <Space y={{ mobile: 20, laptop: 40, laptopLg: 50 }} />
    <AutoSlide
      up
      translate={30}
      css={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: rem(30), // needed because of overflow: 'hidden on the parent element
      }}
    >
      <WideText as={Link} to="/awards">
        AWARDS AND RECOGNITION
      </WideText>
    </AutoSlide>
  </Wrapper>
)

const NewsSection = ({ images: { clutchHexagon }, ...props }) => (
  <Wrapper padded as="section" css={{ overflow: 'hidden' }} {...props}>
    <BackgroundText>NEWS</BackgroundText>
    <div css={{ display: 'flex', alignItems: 'center' }}>
      <AutoScale
        scale={0.92}
        delay={100}
        css={{
          display: 'grid',
          justifyItems: 'center',
          alignItems: 'center',
          width: '20%',
          [media.max.tabletMd]: { display: 'none' },
        }}
      >
        <div
          css={{
            gridArea: '1 / 1',
            width: '100%',
            transform: 'scale(2.4)',
          }}
        >
          <Img fluid={clutchHexagon} />
        </div>
        <img
          css={{
            gridArea: '1 / 1',
            display: 'block',
            width: '70%',
          }}
          src={topClutchLogo}
          alt="Top Clutch logo"
        />
      </AutoScale>
      <div css={{ [media.tabletMd]: { width: '80%' } }}>
        <AutoSlide left css={{ position: 'relative', zIndex: 2 }}>
          <Headline
            css={{
              fontSize: rem(22),
              [media.mobileLg]: { fontSize: rem(22) },
              [media.tabletMd]: { fontSize: '3.7vmax' },
            }}
          >
            Named Top Los Angeles
            <br />
            Branding Agency 2018
          </Headline>
        </AutoSlide>
        <Space y={theme.space.lg} />
        <AutoSlide right translate={50}>
          <WideText as={Link} to="/agency">
            READ OUR CLUTCH REVIEWS <NextArrow width="1.9em" />
          </WideText>
        </AutoSlide>
      </div>
    </div>
    <Space y={{ tabletMd: 200 }} />
  </Wrapper>
)

const TileCell = ({ even, children, ...props }) =>
  React.cloneElement(React.Children.only(children), {
    css: {
      width: '80%',
      marginLeft: '10%',
      [media.tabletMd]: {
        width: '66.666%',
        marginLeft: '16.666%',
      },
      [media.laptop]: {
        marginLeft: even ? '33.333%' : '16.666%',
      },
      overflow: 'hidden',
    },
    ...props,
  })

export const query = graphql`
  query {
    davidFace: file(relativePath: { eq: "pages/home/david-face.png" }) {
      ...textContainerWide
    }
    clutchHexagon: file(relativePath: { eq: "pages/home/clutch-hexagon.png" }) {
      ...textContainerWide
    }
    motivRing: file(relativePath: { eq: "pages/work/motiv-ring.jpg" }) {
      ...desktopProjectTileImage
    }
    azubu: file(relativePath: { eq: "pages/work/azubu.jpg" }) {
      ...desktopProjectTileImage
    }
    amcPreacher: file(relativePath: { eq: "pages/work/amc-preacher.jpg" }) {
      ...desktopProjectTileImage
    }
    tennisChannel: file(relativePath: { eq: "pages/work/tennis-channel.jpg" }) {
      ...desktopProjectTileImage
    }
    espn: file(relativePath: { eq: "pages/work/espn.jpg" }) {
      ...desktopProjectTileImage
    }
    nestea: file(relativePath: { eq: "pages/work/nestea.jpg" }) {
      ...desktopProjectTileImage
    }
  }
`

export default ({ data }) => {
  useNavbarTheme('light')

  const davidFace = data.davidFace.childImageSharp.fluid
  const clutchHexagon = data.clutchHexagon.childImageSharp.fluid

  const projects = {
    motivRing: {
      ...getProject('motiv-ring'),
      image: data.motivRing.childImageSharp.fluid,
    },
    azubu: {
      ...getProject('azubu'),
      image: data.azubu.childImageSharp.fluid,
    },
    amcPreacher: {
      ...getProject('amc-preacher'),
      image: data.amcPreacher.childImageSharp.fluid,
    },
    tennisChannel: {
      ...getProject('tennis-channel'),
      image: data.tennisChannel.childImageSharp.fluid,
    },
    espn: {
      ...getProject('espn'),
      image: data.espn.childImageSharp.fluid,
    },
    nestea: {
      ...getProject('nestea'),
      image: data.nestea.childImageSharp.fluid,
    },
  }

  useEffect(() => {
    Object.values(projects).forEach(({ key }) => {
      global.___loader.loadPage(parsePath(projectURL(key)).pathname)
    })
  }, [])

  return (
    <Fragment>
      <SEO />
      <Header />
      <Shell>
        <ContentAboveFooter>
          <Wrapper
            css={{
              paddingBottom: rem(65),
              [media.laptopLg]: { paddingBottom: theme.space.xxl },

              '> * + *': {
                marginTop: rem(65),
                [media.laptopLg]: { marginTop: theme.space.xxl },
              },
            }}
          >
            <TileCell
              css={{
                '--y': rem(-40),
                [media.laptopSm]: { '--y': rem(-64) },
                [min(1200)]: { '--y': rem(-100) },
                [media.laptopLg]: { '--y': rem(-120) },
                transform: 'translateY(var(--y))',
                marginBottom: 'var(--y)',
              }}
            >
              <ProjectTile
                textType="headline"
                data={projects.motivRing}
                offsetTitle={{ mobile: 30, tablet: 50 }}
              />
            </TileCell>
            <TileCell even>
              <ProjectTile textType="headline" data={projects.azubu} />
            </TileCell>
            <AgencySection
              images={{ davidFace }}
              css={{ [media.max.tabletMd]: { display: 'none' } }}
            />
            {/* <TileCell>
              <ProjectTile textType="headline" data={projects.motivRing} />
            </TileCell>
            <TileCell even>
              <ProjectTile textType="headline" data={projects.azubu} />
            </TileCell>
            <ClientLogosSection /> */}
            <TileCell>
              <ProjectTile textType="headline" data={projects.amcPreacher} />
            </TileCell>
            <TileCell even>
              <ProjectTile textType="headline" data={projects.tennisChannel} />
            </TileCell>
            <AwardLogosSection />
            <TileCell>
              <ProjectTile textType="headline" data={projects.espn} />
            </TileCell>
            <TileCell even>
              <ProjectTile textType="headline" data={projects.nestea} />
            </TileCell>
            <NewsSection images={{ clutchHexagon }} />
          </Wrapper>
        </ContentAboveFooter>
        <ChallengeUsFooter />
        <HelloFab />
      </Shell>
    </Fragment>
  )
}
